import { useMemo, useState } from "react";
import { format } from "date-fns";
import { Eye, Link2 } from "lucide-react";

import { DataTable } from "components/ui/DataTable";
import { Dialog } from "components/ui/Dialog";
import { Pill, PILL_COLORS } from "components/ui/Pill";
import { HealthlinkXsltFrame } from "features/healthlink/components/HealthlinkXsltFrame/HealthlinkXsltFrame";
import { useUnlinkedMncmsMessages } from "features/pregnancy/hooks/useUnlinkedMncmsMessages";
import {
  describeUnlinkedState,
  getUnlinkedState,
  UNLINKED_STATES,
} from "features/pregnancy/utils/unlinkedMessageState";

import { AssignMessageDialog } from "./AssignMessageDialog";
import styles from "./NeedsAttentionTable.module.css";
import { UnlinkedPatientName } from "./UnlinkedPatientName";

const ROWS_OPTIONS = [10, 20, 50];

const STATE_PILL = {
  [UNLINKED_STATES.noPatient]: PILL_COLORS.statusError,
  [UNLINKED_STATES.noPregnancy]: PILL_COLORS.statusWarning,
};

const formatDateTime = (value) => {
  if (!value) return "-";
  try {
    return format(new Date(value), "dd MMM yyyy, HH:mm");
  } catch {
    return value;
  }
};

const StatePill = ({ state }) => {
  const description = describeUnlinkedState(state);

  if (!description) return null;

  return <Pill {...STATE_PILL[state]}>{description.label}</Pill>;
};

export const NeedsAttentionTable = () => {
  const [rows, setRows] = useState(10);
  const [previewedMessage, setPreviewedMessage] = useState(null);
  const [assignedMessage, setAssignedMessage] = useState(null);

  const { data, isLoading } = useUnlinkedMncmsMessages();
  const messages = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const rowActions = useMemo(
    () => [
      // View first and always available: whoever assigns has to read the message
      // to know whose it is, so reading must not require assigning first.
      {
        label: "View",
        icon: Eye,
        primary: true,
        onClick: (message) => setPreviewedMessage(message),
      },
      {
        label: "Assign",
        icon: Link2,
        primary: true,
        onClick: (message) => setAssignedMessage(message),
      },
    ],
    []
  );

  const columns = useMemo(
    () => [
      {
        id: "receivedAt",
        header: "Received",
        width: "170px",
        render: (message) => formatDateTime(message.receivedAt),
      },
      {
        id: "state",
        header: "Needs",
        width: "140px",
        render: (message) => <StatePill state={getUnlinkedState(message)} />,
      },
      {
        id: "patient",
        header: "Patient",
        width: "180px",
        render: (message) => (
          <UnlinkedPatientName patientId={message.patientId} />
        ),
      },
      {
        id: "hospital",
        header: "Hospital",
        width: "200px",
        render: (message) =>
          message.hospitalName || <span className={styles.muted}>-</span>,
      },
      {
        id: "summary",
        header: "Summary",
        width: "200px",
        flexGrow: 1,
        render: (message) => (
          <span className={styles.summary} title={message.summary || ""}>
            {message.summary || "-"}
          </span>
        ),
      },
    ],
    []
  );

  const previewState = getUnlinkedState(previewedMessage);
  const previewDescription = describeUnlinkedState(previewState);
  const hasPreview =
    !!previewedMessage?.hl7MessageContent && !!previewedMessage?.xslContent;

  return (
    <div className={styles.wrapper}>
      <div className={styles.legend}>
        {[UNLINKED_STATES.noPatient, UNLINKED_STATES.noPregnancy].map(
          (state) => (
            <div key={state} className={styles.legendRow}>
              <StatePill state={state} />
              <span>{describeUnlinkedState(state).detail}</span>
            </div>
          )
        )}
      </div>

      <DataTable
        columns={columns}
        data={messages}
        getRowId={(message) => message.id}
        isLoading={isLoading}
        noScroll
        rowActions={rowActions}
        emptyTitle="Nothing needs attention"
        emptyText="Every maternity message we've received is attached to a patient and a pregnancy."
        rowsValue={rows}
        onRowsChange={setRows}
        rowsOptions={ROWS_OPTIONS}
      />

      <Dialog
        open={!!previewedMessage}
        onClose={() => setPreviewedMessage(null)}
        title="Maternity message"
        width={1100}
      >
        <div className={styles.detail}>
          <div className={styles.detailMeta}>
            <div className={styles.detailField}>
              <span className={styles.detailLabel}>Received</span>
              <span className={styles.detailValue}>
                {formatDateTime(previewedMessage?.receivedAt)}
              </span>
            </div>
            <div className={styles.detailField}>
              <span className={styles.detailLabel}>Hospital</span>
              <span className={styles.detailValue}>
                {previewedMessage?.hospitalName || "-"}
              </span>
            </div>
            <div className={styles.detailField}>
              <span className={styles.detailLabel}>Message id</span>
              <span className={styles.detailValue}>
                {previewedMessage?.messageControlId || "-"}
              </span>
            </div>
            <div className={styles.detailField}>
              <span className={styles.detailLabel}>Needs</span>
              <span className={styles.detailValue}>
                {previewDescription?.detail}
              </span>
            </div>
          </div>

          {hasPreview ? (
            <HealthlinkXsltFrame
              xml={previewedMessage.hl7MessageContent}
              xsl={previewedMessage.xslContent}
            />
          ) : (
            <p className={styles.previewUnavailable}>
              The rendered message is unavailable — the hospital&apos;s message or
              its stylesheet didn&apos;t reach us. The summary above is what we
              hold.
            </p>
          )}
        </div>
      </Dialog>

      <AssignMessageDialog
        message={assignedMessage}
        onClose={() => setAssignedMessage(null)}
        onAssigned={() => setAssignedMessage(null)}
      />
    </div>
  );
};
